// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// Collapsible deployment alert component for stale bundle notifications.
// Shows a blue info alert that can be collapsed to just an icon.
// When collapsed, the alert wiggles periodically to remind the user.
import {
  ChevronLeftRounded,
  ChevronRightRounded,
  InfoOutlined,
  RefreshRounded,
} from '@/constants/hardfin-standard-icons';
import { Alert, Box, IconButton, Snackbar } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

// Wiggle animation timing constants (in milliseconds)
const INITIAL_WIGGLE_DELAY_MS = 1000;
const WIGGLE_INTERVAL_MS = 60000;
const WIGGLE_DURATION_MS = 1000;

interface Props {
  /**
   * Whether the alert should be visible.
   */
  open: boolean;
  /**
   * Callback when the user clicks the reload button.
   */
  onReload: () => void;
}

/**
 * CollapsibleDeploymentAlert displays a deployment update notification
 * that can be collapsed to save screen space. When collapsed, it shows
 * only the info icon and periodically wiggles to remind the user that
 * updates are available.
 */
export function CollapsibleDeploymentAlert(props: Props) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [shouldWiggle, setShouldWiggle] = useState(false);
  const wiggleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initialWiggleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const handleCollapse = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  const handleExpand = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  // Trigger wiggle animation periodically when collapsed
  useEffect(() => {
    if (!isCollapsed || !props.open) {
      // Clear any existing timers when expanded or closed
      if (wiggleIntervalRef.current) {
        clearInterval(wiggleIntervalRef.current);
        wiggleIntervalRef.current = null;
      }
      if (initialWiggleTimeoutRef.current) {
        clearTimeout(initialWiggleTimeoutRef.current);
        initialWiggleTimeoutRef.current = null;
      }
      setShouldWiggle(false);
      return;
    }

    // Initial wiggle after collapse
    initialWiggleTimeoutRef.current = setTimeout(() => {
      setShouldWiggle(true);
      setTimeout(() => setShouldWiggle(false), WIGGLE_DURATION_MS);
    }, INITIAL_WIGGLE_DELAY_MS);

    // Periodic wiggle every 60 seconds
    wiggleIntervalRef.current = setInterval(() => {
      setShouldWiggle(true);
      setTimeout(() => setShouldWiggle(false), WIGGLE_DURATION_MS);
    }, WIGGLE_INTERVAL_MS);

    return () => {
      if (wiggleIntervalRef.current) {
        clearInterval(wiggleIntervalRef.current);
        wiggleIntervalRef.current = null;
      }
      if (initialWiggleTimeoutRef.current) {
        clearTimeout(initialWiggleTimeoutRef.current);
        initialWiggleTimeoutRef.current = null;
      }
    };
  }, [isCollapsed, props.open]);

  if (!props.open) {
    return null;
  }

  if (isCollapsed) {
    return (
      <Snackbar open>
        <Box
          onClick={handleExpand}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 1,
            bgcolor: 'info.lighter',
            border: 1,
            borderColor: 'info.light',
            borderRadius: 1,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            animation: shouldWiggle ? 'wiggle 0.6s ease-in-out' : 'none',
            '@keyframes wiggle': {
              '0%, 100%': { transform: 'rotate(0deg)' },
              '15%': { transform: 'rotate(-8deg)' },
              '30%': { transform: 'rotate(8deg)' },
              '45%': { transform: 'rotate(-6deg)' },
              '60%': { transform: 'rotate(6deg)' },
              '75%': { transform: 'rotate(-3deg)' },
              '90%': { transform: 'rotate(3deg)' },
            },
            '&:hover': {
              bgcolor: 'info.light',
            },
          }}
        >
          <InfoOutlined fontSize="small" color="info" />
          <ChevronRightRounded fontSize="small" color="info" />
        </Box>
      </Snackbar>
    );
  }

  return (
    <Snackbar open>
      <Alert
        severity="info"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="small" onClick={props.onReload} sx={{ px: 1 }}>
              <RefreshRounded fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleCollapse} sx={{ px: 1 }}>
              <ChevronLeftRounded fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        Hardfin updates are available. Reload to access the latest version.
      </Alert>
    </Snackbar>
  );
}
